"use client";

import { TbMonogram } from "@/components/brand/tb-monogram";
import { SkeletonRegion } from "@/components/ui/skeleton";
import { PANEL_LAYER_CLASS } from "@/lib/host-panel/layer-stack";
import { cn } from "@/lib/utils";

/**
 * What fills the gap between the tap and a lazily loaded layer.
 *
 * Layers that enter the tree only after the tap arrive by dynamic import (criterion
 * 23.6): without this the tap produces nothing until the chunk lands, and an
 * unanswered tap reads as an unregistered one. `SkeletonRegion` owns the live region.
 */
export function LayerLoading({ label }: { label: string }) {
  return (
    <SkeletonRegion
      label={label}
      className={cn(
        "fixed inset-0 grid place-content-center",
        PANEL_LAYER_CLASS.pageLoader,
        "bg-[color-mix(in_srgb,var(--tb-oxide)_38%,transparent)]",
        // Delayed reveal, same principle as the statement skeleton: a layer that lands
        // fast flashes no veil at all.
        "opacity-0 motion-safe:animate-[tb-veil-in_var(--tb-duration-2)_var(--tb-ease-linen)_var(--tb-duration-3)_forwards]",
      )}
    >
      <span
        aria-hidden="true"
        data-mono-spin=""
        className="block w-[64px] animate-[tb-mono-breathe_1.9s_var(--tb-ease-linen)_infinite] text-[var(--tb-porcelain)]"
      >
        <TbMonogram className="w-full" />
      </span>
    </SkeletonRegion>
  );
}
